import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FeedbackModal from "@/components/FeedbackModal";
import { Flame, UserPlus, Star } from "lucide-react";

/**
 * Item 4/5 (2026-07): the three engagement nudges (streak / invite friends /
 * leave feedback) merged into ONE compact horizontal strip — one chip each.
 * Rendered ONLY for individuals (Dashboard gates it; orgs get no volunteer
 * gamification). The feedback chip appears only when a review is actually due.
 * The streak toast fires at most once: the backend check-in is atomic, so
 * StrictMode's doubled effect sees incremented=true exactly once.
 */
function Chip({ testId, icon: Icon, children, onClick, title }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={title}
      className={`flex-1 min-w-[180px] inline-flex items-center gap-2 bg-white border border-neutral-200 rounded-full px-4 py-2.5 text-sm text-forest transition-colors text-left ${onClick ? "hover:border-forest/40" : "cursor-default"}`}
    >
      <Icon className="w-4 h-4 text-terracotta shrink-0" />
      <span className="truncate">{children}</span>
    </button>
  );
}

export function NudgeStrip() {
  const [streak, setStreak] = useState(null);
  const [ref, setRef] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [target, setTarget] = useState(null);

  const loadFeedback = () =>
    api.feedbackPending().then((d) => setFeedback(d.items || [])).catch(() => setFeedback([]));

  useEffect(() => {
    api.checkIn().then((r) => {
      setStreak(r);
      if (r.incremented && r.streak_current > 1) toast.success("Streak +1 — you're on fire!");
    }).catch(() => {});
    api.myReferral().then(setRef).catch(() => {});
    loadFeedback();
  }, []);

  const copyRef = async () => {
    if (!ref?.referral_url) return;
    try {
      await navigator.clipboard.writeText(ref.referral_url);
      toast.success("Referral link copied — send it to a friend");
      api.trackReferralShare("dashboard").catch(() => {});
      setRef((r) => ({ ...r, shares: (r?.shares || 0) + 1 }));
    } catch { toast.error("Copy failed — try again"); }
  };

  const joined = ref?.count || 0;
  return (
    <div data-testid="nudge-strip" className="mt-8 flex flex-wrap gap-3">
      <Chip
        testId="streak-chip"
        icon={Flame}
        title={streak?.streak_best ? `Best streak: ${streak.streak_best} days — visit daily to keep the flame alive` : "Visit your dashboard every day to keep the flame alive"}
      >
        <span data-testid="streak-current">
          <b>{streak?.streak_current ?? "—"}</b>-day streak
        </span>
      </Chip>
      <Chip testId="invite-chip" icon={UserPlus} onClick={copyRef} title={ref?.referral_url || "Loading your invite link…"}>
        Invite friends{joined > 0 ? <span data-testid="referral-count"> — <b>{joined}</b> joined</span> : " — copy your link"}
      </Chip>
      {feedback.length > 0 && (
        <Chip testId="feedback-chip" icon={Star} onClick={() => setTarget(feedback[0])}>
          Leave feedback ({feedback.length})
        </Chip>
      )}
      <FeedbackModal
        open={!!target}
        item={target}
        onClose={() => setTarget(null)}
        onSubmitted={() => { setTarget(null); loadFeedback(); }}
      />
    </div>
  );
}
